import React, { useState, useRef, useEffect } from "react";
import {
  TabsContainer,
  TabsWrapper,
  TabItem,
  InkBar,
  TabDot,
} from "./Tabs.styles";
import { TabsProps } from "./Tabs.types";
import { Skeleton } from "@mui/material";
import {
  Body5MediumTypography,
  Caps2BoldTypography,
} from "../../primitives/Typography/typographyVariants";
import FlexRow from "../Layout/FlexRow/FlexRow";
import Box from "../../primitives/Box/Box";
import { colorGuide } from "../../theme/colors";
import { NumberPill } from "../NumberPill/NumberPill";

export const Tabs: React.FC<TabsProps> = ({
  tabs,
  selectedTabId,
  onTabChange,
  isLoading = false,
  skipFirstTab = false,
}) => {
  const getSelectedIndex = (tabId: number) => {
    let index = tabs.findIndex((tab) => tab.id === tabId);
    return index !== -1 ? index : 0;
  };
  const [inkBarStyle, setInkBarStyle] = useState({ left: 0, width: 0 });
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [selectedTabIndex, setSelectedTabIndex] = useState(
    getSelectedIndex(selectedTabId)
  );

  // Handle tab change
  const changeTab = (index: number) => {
    if (index >= 0 && index < tabs.length) {
      setSelectedTabIndex(index);
      onTabChange(tabs[index].id);
    }
  };

  // Update InkBar position whenever tab or loading state changes
  useEffect(() => {
    const activeTab = tabRefs.current[selectedTabIndex];
    if (activeTab) {
      setInkBarStyle({
        left: activeTab.offsetLeft,
        width: activeTab.offsetWidth,
      });
    }
  }, [selectedTabIndex, isLoading, tabs]); // Include `tabs` in dependency array to trigger recalculation

  useEffect(() => {
    const newIndex = getSelectedIndex(selectedTabId);
    if (newIndex !== -1 && newIndex !== selectedTabIndex) {
      setSelectedTabIndex(newIndex);
    }
  }, [selectedTabId, tabs]);

  return (
    <>
      {/* Tabs Navigation */}
      <TabsContainer>
        <TabsWrapper>
          {tabs.map((tab, index) => (
            <TabItem
              key={tab.id}
              ref={(el: any) => (tabRefs.current[index] = el)}
              className={tab.className}
              isActive={selectedTabIndex === index}
              onClick={() => changeTab(index)}
              disabled={!tab?.clickable}
              data-cy={tab?.dataCy}
              data-selected={selectedTabIndex === index ? "true" : "false"}
            >
              {isLoading ? (
                <Skeleton width="80px" height="18px" />
              ) : (
                <FlexRow alignItems="center" justifyContent="center">
                  {tab?.showDot && tab?.dotColor && (
                    <TabDot color={tab.dotColor} />
                  )}
                  <Body5MediumTypography textAlign="center">
                    {tab?.showPillNumber
                      ? `${tab?.name} ${isLoading ? "" : `(${tab?.number})`}`
                      : tab?.label}
                  </Body5MediumTypography>
                  {tab?.badgeText && (
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      ml="8px"
                    >
                      <NumberPill
                        value={tab.badgeText}
                        backgroundColor={
                          tab.badgeColor ||
                          colorGuide.lightComponents.swipeableTabs.badgeColor
                        }
                        textColor={
                          tab.badgeTextColor ||
                          colorGuide.lightComponents.swipeableTabs
                            .badgeTextColor
                        }
                        minHeight="16px"
                        fontSize="10px"
                        fontWeight={700}
                        lineHeight="1"
                      />
                    </Box>
                  )}
                </FlexRow>
              )}
            </TabItem>
          ))}
          <InkBar style={inkBarStyle} />
        </TabsWrapper>
      </TabsContainer>
    </>
  );
};
